import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { GoogleLogo, Envelope, Lock, ArrowRight } from "phosphor-react";
import { loginWithGoogle, API_URL } from "../auth";
import { bumpAuthVersion } from "../utils/authSignal";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_tradespro-1/artifacts/b15t91h1_Free-Service-Pro-Logo.png";

export default function LandingPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Handle Google Login - routes through unified auth
  const handleGoogleLogin = async () => {
    const { error } = await loginWithGoogle('/dashboard');
    if (error) {
      toast.error(error.message || "Failed to start Google sign-in");
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.token) {
          localStorage.setItem('auth_token', data.token);
        }
        
        // Bump auth version to notify other tabs (with identity for tracking)
        bumpAuthVersion('login', { email: data.email, role: data.role });
        
        toast.success("Welcome back!");
        if (data.role === "platform_admin") {
          navigate("/admin", { state: { user: data } });
        } else if (data.role === "technician") {
          navigate("/tech", { state: { user: data } });
        } else {
          navigate("/dashboard", { state: { user: data } });
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Invalid credentials");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Failed to login. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src={LOGO_URL}
            alt="Free Service Pro"
            className="max-w-sm w-full mx-auto object-contain"
          />
        </div>

        {/* Login Card */}
        <div className="bg-white border-2 border-zinc-200 p-8 rounded-sm shadow-sm">
          <div className="text-center mb-8">
            <h1 className="font-heading text-2xl font-bold uppercase text-zinc-900">
              Welcome
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Sign in to manage your business
            </p>
          </div>

          {/* Google Login */}
          <Button
            type="button"
            onClick={handleGoogleLogin}
            className="w-full h-12 bg-white border-2 border-zinc-300 text-zinc-900 hover:bg-zinc-50 font-medium mb-6"
            data-testid="google-login-btn"
          >
            <GoogleLogo size={20} weight="bold" className="mr-2" />
            Continue with Google
          </Button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-200"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-zinc-400">Or continue with email</span>
            </div>
          </div>

          {/* Email Login Form */}
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1">
                <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="pl-10 h-12 border-2"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-[#FF6600] hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 h-12 border-2"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#FF6600] hover:bg-[#CC5200] text-white font-medium"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in..." : "Sign In"}
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </form>

          {/* Sign Up Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-zinc-500">
              Don't have an account?{" "}
              <Link to="/signup" className="text-[#FF6600] font-medium hover:underline" data-testid="signup-link">
                Start free today
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-zinc-400">
          <p>© 2025 Free Service Pro. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <a href="#" className="hover:text-zinc-600">Terms</a>
            <a href="#" className="hover:text-zinc-600">Privacy</a>
            <a href="#" className="hover:text-zinc-600">Support</a>
          </div>
        </div>
      </div>
    </div>
  );
}
